import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  DisbursementCancellationRequirement,
  DisbursementCancellationRequirementListParams,
  DisbursementCancellationRequirementListResponse,
  ScheduleCorrectionRequestListParams,
  ScheduleCorrectionRequestListResponse,
  ScheduleCorrectionRequestModel,
  ScheduleCorrectionActiveNoteParams,
  ScheduleCorrectionActiveNoteResponse,
} from '../../models/schedule/schedule-correction-requests.model';

@Injectable({
  providedIn: 'root',
})
export class ScheduleCorrectionRequestsService {
  private readonly apiUrl = '/schedule_submission_correction_requests';
  private readonly cancellationRequirementsUrl = '/disbursement_cancellation_requirements';
  private readonly http = inject(HttpClient);

  getCorrectionRequests(
    params?: ScheduleCorrectionRequestListParams,
  ): Observable<ScheduleCorrectionRequestListResponse> {
    return this.http.get<unknown>(this.apiUrl, {
      params: this.buildListParams(params),
    }).pipe(
      map((res) => this.normalizeListResponse(res, params)),
    );
  }

  getCorrectionRequest(id: number): Observable<ScheduleCorrectionRequestModel> {
    return this.http.get<ScheduleCorrectionRequestModel>(`${this.apiUrl}/${id}`);
  }

  getActiveCorrectionRequestForEditing(params: {
    subject_schedule_group_id?: number | string | null;
    project_research_subject_id?: number | string | null;
  }): Observable<ScheduleCorrectionRequestModel> {
    return this.http.get<ScheduleCorrectionRequestModel>(
      `${this.apiUrl}/active_for_editing`,
      { params: this.buildActiveForEditingParams(params) },
    );
  }

  getActiveNewCourseNotes(
    params?: ScheduleCorrectionActiveNoteParams,
  ): Observable<ScheduleCorrectionActiveNoteResponse> {
    return this.http.get<ScheduleCorrectionActiveNoteResponse>(
      `${this.apiUrl}/active_new_course_notes`,
      { params: this.buildParams(params) },
    );
  }

  approveCorrectionRequest(id: number, comment?: string | null): Observable<ScheduleCorrectionRequestModel> {
    return this.http.post<ScheduleCorrectionRequestModel>(
      `${this.apiUrl}/${id}/vice_dean_approve`,
      { comment: comment ?? null },
    );
  }

  rejectCorrectionRequest(id: number, comment: string): Observable<ScheduleCorrectionRequestModel> {
    return this.http.post<ScheduleCorrectionRequestModel>(
      `${this.apiUrl}/${id}/vice_dean_reject`,
      { comment },
    );
  }

  openEditing(id: number, comment?: string | null): Observable<ScheduleCorrectionRequestModel> {
    return this.http.post<ScheduleCorrectionRequestModel>(
      `${this.apiUrl}/${id}/open_editing`,
      { comment: comment ?? null },
    );
  }

  getCancellationRequirements(
    params?: DisbursementCancellationRequirementListParams,
  ): Observable<DisbursementCancellationRequirementListResponse> {
    return this.http.get<unknown>(this.cancellationRequirementsUrl, {
      params: this.buildCancellationRequirementParams(params),
    }).pipe(
      map((res) => this.normalizeCancellationRequirementListResponse(res, params)),
    );
  }

  financeCancelDisbursement(id: number): Observable<DisbursementCancellationRequirement> {
    return this.http.post<DisbursementCancellationRequirement>(
      `${this.apiUrl}/${id}/finance_cancel_disbursement`,
      {},
    );
  }

  private buildListParams(params?: ScheduleCorrectionRequestListParams): HttpParams {
    let httpParams = new HttpParams();
    const supportedParams: (keyof ScheduleCorrectionRequestListParams)[] = [
      'status',
      'academic_term_id',
      'keywords',
      'page',
      'limit',
      'sort',
      'direction',
    ];

    supportedParams.forEach((key) => {
      const value = params?.[key];
      if (value != null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  private buildActiveForEditingParams(params: {
    subject_schedule_group_id?: number | string | null;
    project_research_subject_id?: number | string | null;
  }): HttpParams {
    let httpParams = new HttpParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value != null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  private buildParams(params?: object): HttpParams {
    let httpParams = new HttpParams();

    Object.entries(params ?? {}).forEach(([key, value]) => {
      if (value != null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  private buildCancellationRequirementParams(
    params?: DisbursementCancellationRequirementListParams,
  ): HttpParams {
    let httpParams = new HttpParams();
    const supportedParams: (keyof DisbursementCancellationRequirementListParams)[] = [
      'status',
      'academic_term_id',
      'department_id',
      'keywords',
      'page',
      'limit',
    ];

    supportedParams.forEach((key) => {
      const value = params?.[key];
      if (value != null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  private normalizeListResponse(
    res: unknown,
    params?: ScheduleCorrectionRequestListParams,
  ): ScheduleCorrectionRequestListResponse {
    if (res !== null && typeof res === 'object' && !Array.isArray(res)) {
      const response = res as Record<string, unknown>;

      if (Array.isArray(response['data'])) {
        return {
          data: response['data'] as ScheduleCorrectionRequestModel[],
          page: Number(response['page'] ?? params?.page ?? 1),
          limit: Number(response['limit'] ?? params?.limit ?? 10),
          total: Number(response['total'] ?? response['data'].length),
          total_pages: Number(response['total_pages'] ?? response['total_page'] ?? 1),
          summary: response['summary'] as ScheduleCorrectionRequestListResponse['summary'],
        };
      }
    }

    const data = Array.isArray(res) ? res as ScheduleCorrectionRequestModel[] : [];
    const limit = params?.limit ?? 10;

    return {
      data,
      page: params?.page ?? 1,
      limit,
      total: data.length,
      total_pages: Math.max(1, Math.ceil(data.length / limit)),
    };
  }

  private normalizeCancellationRequirementListResponse(
    res: unknown,
    params?: DisbursementCancellationRequirementListParams,
  ): DisbursementCancellationRequirementListResponse {
    if (res !== null && typeof res === 'object' && !Array.isArray(res)) {
      const response = res as Record<string, unknown>;
      const requirementsValue = response['requirements'] ?? response['data'];
      const requirements = Array.isArray(requirementsValue)
        ? requirementsValue as DisbursementCancellationRequirement[]
        : [];
      const summary = response['summary'] as DisbursementCancellationRequirementListResponse['summary'];
      const total = Number(response['total'] ?? requirements.length);
      const limit = Number(response['limit'] ?? params?.limit ?? 10);

      return {
        requirements,
        page: Number(response['page'] ?? params?.page ?? 1),
        limit,
        total,
        total_pages: Number(response['total_pages'] ?? response['total_page'] ?? Math.max(1, Math.ceil(total / limit))),
        summary,
      };
    }

    const requirements = Array.isArray(res)
      ? res as DisbursementCancellationRequirement[]
      : [];
    const limit = params?.limit ?? 10;

    return {
      requirements,
      page: params?.page ?? 1,
      limit,
      total: requirements.length,
      total_pages: Math.max(1, Math.ceil(requirements.length / limit)),
    };
  }
}
